import { useCallback, useEffect, useRef, useState } from "react";
import { logConnectorFailure, logConnectorSuccess } from "../../../../../telemetry";
import { DETAIL_ERROR_MESSAGES, PDF_CONFIG } from "../../../constants";

// Loaded lazily (not at module scope) so mammoth's parsing bundle is only
// pulled in once a user actually opens the Compare tab — mirrors
// usePdfManager.ts's identical rationale for lazily loading pdfjs-dist.
let mammothPromise: Promise<typeof import("mammoth")> | null = null;
function loadMammoth(): Promise<typeof import("mammoth")> {
  mammothPromise ??= import("mammoth");
  return mammothPromise;
}

const DOCX_LOAD_TIMEOUT_MS = 20_000;

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

/** Fetches a converted .docx and renders it as sandboxed preview HTML via mammoth. */
export function useDocxPreview() {
  const [html, setHtml] = useState("");
  const [isLoadingDocx, setIsLoadingDocx] = useState(false);
  const [docxError, setDocxError] = useState("");
  const [isDocxLoaded, setIsDocxLoaded] = useState(false);
  const [zoomLevel, setZoomLevel] = useState<number>(PDF_CONFIG.DEFAULT_ZOOM);
  const isUnmountedRef = useRef(false);

  const zoomIn = useCallback(() => setZoomLevel((prev) => Math.min(prev + PDF_CONFIG.ZOOM_STEP, PDF_CONFIG.MAX_ZOOM)), []);
  const zoomOut = useCallback(() => setZoomLevel((prev) => Math.max(prev - PDF_CONFIG.ZOOM_STEP, PDF_CONFIG.MIN_ZOOM)), []);
  const resetZoom = useCallback(() => setZoomLevel(PDF_CONFIG.DEFAULT_ZOOM), []);
  const setZoom = useCallback(
    (level: number) => setZoomLevel(Math.min(Math.max(level, PDF_CONFIG.MIN_ZOOM), PDF_CONFIG.MAX_ZOOM)),
    [],
  );

  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  const loadDocx = useCallback(async (url: string): Promise<void> => {
    if (!isValidUrl(url)) {
      setDocxError(DETAIL_ERROR_MESSAGES.INVALID_DOCX_URL);
      return;
    }

    setIsLoadingDocx(true);
    setDocxError("");

    try {
      const [mammoth, response] = await withTimeout(
        Promise.all([loadMammoth(), fetch(url)]),
        DOCX_LOAD_TIMEOUT_MS,
        "Document load timed out",
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch converted document: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });

      if (isUnmountedRef.current) return;
      setHtml(result.value);
      setIsDocxLoaded(true);
      logConnectorSuccess("docuparser", "docx-load");
    } catch (err) {
      if (isUnmountedRef.current) return;
      const isTimeout = err instanceof Error && err.message === "Document load timed out";
      setDocxError(
        isTimeout ? "Loading the converted document timed out. Please retry." : DETAIL_ERROR_MESSAGES.DOCX_LOAD_FAILED,
      );
      logConnectorFailure("docuparser", err, isTimeout ? "docx-load-timeout" : "docx-load-failed");
    } finally {
      if (!isUnmountedRef.current) setIsLoadingDocx(false);
    }
  }, []);

  const resetDocx = useCallback(() => {
    setHtml("");
    setDocxError("");
    setIsDocxLoaded(false);
    setZoomLevel(PDF_CONFIG.DEFAULT_ZOOM);
  }, []);

  return { html, isLoadingDocx, docxError, isDocxLoaded, loadDocx, resetDocx, zoomLevel, zoomIn, zoomOut, resetZoom, setZoom };
}
